import paper from 'paper';
import { Class } from '../../../providers/class';
import { ExportedPolygon } from '../../../providers/workspace/canvas';
import { NeuralaProject } from './project';
import { Subject } from 'rxjs';

import {
    scaleSouthEast,
    scaleSouthWest,
    scaleNorthEast,
    scaleNorthWest,
    scaleEast,
    scaleSouth,
    scaleNorth,
    scaleWest
} from './bounding-box-utils';

export class Polygon extends paper.CompoundPath {
    // Neurala-Specific Properties

    public neuralaType = 'Polygon';
    public neuralaClass: Class = { name: '', color: '', id: '', datasetIdentifier: '' };
    public polygonIdentifier: string | undefined = undefined;
    public isPrediction = false;
    public fullySelected = false;
    private predictionFlag = false;

    // End Neurala-Specific Properties

    public strokeColor = new paper.Color('rgba(255, 255, 255, 1)');
    public strokeWidth = 3;
    public fillColor = new paper.Color('rgba(255, 255, 255, 0.4)');
    public selectedColor = new paper.Color('rgba(255, 255, 255, 1)');
    public strokeScaling = false;

    public defaultNoClassColor = new paper.Color('rgba(255, 255, 255, 1)');

    public project: NeuralaProject;
    public boundingBoxResizeInProgress = new Subject();

    protected readonly HIGHLIGHT_DIMENSION: number = 20;
    protected readonly BB_MANIPULATOR_WIDTH: number = 10;

    protected readonly BB_TOOL_INFO: string[][] = [
        ['scale_left', 'leftCenter'],
        ['scale_right', 'rightCenter'],
        ['scale_up', 'topCenter'],
        ['scale_down', 'bottomCenter'],
        ['scale_left_down', 'bottomLeft'],
        ['scale_left_up', 'topLeft'],
        ['scale_right_down', 'bottomRight'],
        ['scale_right_up', 'topRight']
    ];

    private highlightedPoint: any = {
        index: -1,
        shape: null
    };

    public mouseDown = false;
    public mouseDownOnManipulationTool = false;
    public bbManipulationToolsVisible = false;
    public bbManipulationTools: {
        toolName: string;
        obj: paper.Path.Rectangle;
        placement: string;
    }[] = [];
    private selectedManipulatorTool: string | null = null;
    public isResizing = false;
    private dimensionTextLocation: paper.Point;

    public bbPoints: string[] = [];

    public isBoundingBox: boolean;
    public isLoadingPrediction: boolean;

    constructor(params: any) {
        super(params);
        this.polygonIdentifier = params.polygonIdentifier;
        this.isPrediction = params.isPrediction;

        this.isBoundingBox = params.forceBoundingBox ? params.forceBoundingBox : this.isPolygonBoundingBox();

        if (params.neuralaClass) {
            const color = new paper.Color(params.neuralaClass.color);
            const fillColor = color.clone();
            fillColor.alpha = this.getFillOpacity();

            this.neuralaClass = params.neuralaClass;
            this.strokeColor = color;
            this.selectedColor = color;
            this.fillColor = fillColor;
        } else if (params.defaultNoClassColor) {
            this.setNoClassColor(params.defaultNoClassColor);
        }

        this.on({
            mousemove: e => {
                this.onPolygonMouseMove(e);
            },
            mouseleave: e => {
                this.onPolygonMouseLeave(e);
            },
            mousedown: e => {
                this.mouseDown = true;
            },
            mouseup: e => {
                this.mouseDown = false;
            }
        });
    }

    public markClassAsPredicted() {
        this.predictionFlag = true;
    }

    public markClassAsNotPredicted() {
        this.predictionFlag = false;
    }

    public isClassPredicted(): boolean {
        return this.predictionFlag;
    }

    public setNoClassColor(noClassColor: string) {
        const color = new paper.Color(noClassColor);
        const fillColor = color.clone();
        fillColor.alpha = this.getFillOpacity();

        if (!this.neuralaClass || !this.neuralaClass.id) {
            this.strokeColor = color;
            this.fillColor = fillColor;
            this.selectedColor = color;
        }
    }

    private onPolygonMouseLeave(event: paper.MouseEvent): void {
        if (!this.fullySelected) {
            return;
        }

        this.removeHighlightPoint();
    }

    private onPolygonMouseMove(event: paper.MouseEvent): void {
        if (!this.fullySelected) {
            return;
        }

        const hitTest = this.hitTest(event.point, { segments: true });

        if (!hitTest) {
            this.removeHighlightPoint();
            return;
        }

        switch (hitTest.type) {
            case 'segment':
                this.highlightPoint(hitTest.segment.index);
                if (this.mouseDown) {
                    (this.project as NeuralaProject).setCursor('grabbing');
                    this.removeHighlightPoint();
                } else {
                    (this.project as NeuralaProject).setCursor('grab');
                }

                break;
            default:
                break;
        }
    }

    /************************
     *  Polygon Management  *
     ************************/

    public setId(id: number): void {
        (this as any)._id = id;
    }

    public remove(): boolean {
        if (this.isBoundingBox) {
            this.removeBoundingBoxManipulationTools();
        }
        const didRemove = super.remove();

        if (didRemove) {
            this.removeHighlightPoint();
        }

        return didRemove;
    }

    public clone(options: any = { insert: false, deep: true }): Polygon {
        const clonedPolygon = super.clone(options) as Polygon;
        clonedPolygon.setId(this.id);

        clonedPolygon.fullySelected = this.fullySelected;
        clonedPolygon.neuralaClass = this.neuralaClass;
        clonedPolygon.neuralaType = this.neuralaType;
        clonedPolygon.predictionFlag = this.predictionFlag;
        clonedPolygon.polygonIdentifier = this.polygonIdentifier;
        clonedPolygon.isBoundingBox = this.isBoundingBox;

        return clonedPolygon as Polygon;
    }

    public getPoints(transform: ((point: paper.Point) => paper.Point) | null = null): number[][][] {
        return this.children.map((child: paper.Path) => {
            return child.segments.map(segment => {
                if (transform) {
                    const transformedPoint = transform(segment.point);
                    return [transformedPoint.x, transformedPoint.y];
                }

                return [segment.point.x, segment.point.y];
            });
        });
    }

    public export(): ExportedPolygon {
        return {
            clientIdentifier: this.id,
            classIdentifier: this.neuralaClass.id,
            className: this.neuralaClass.name,
            polygonIdentifier: this.polygonIdentifier,
            data: null
        };
    }

    public exportForRedraw(): ExportedPolygon {
        return Object.assign(this.export(), {
            predictionFlag: this.predictionFlag
        });
    }

    public activate(): void {
        if (this.isBoundingBox) {
            this.generateBoundingBoxManipulationTools();
        } else {
            this.fullySelected = true;
        }
    }

    public deactivate(): void {
        this.fullySelected = false;
        this.removeHighlightPoint();
        if (this.bbManipulationTools.length > 0) {
            this.removeBoundingBoxManipulationTools();
        }
    }

    public close() {
        this.closed = true;
    }

    /**
     * Gets the first child in the polygon
     * In this case, the first child is
     * always going to be the outer contour
     */
    public getOuterContour(): paper.Path {
        return this.children[0] as paper.Path;
    }

    private generateHighlightForPoint(segment: paper.Segment): paper.Path.Rectangle {
        const dimension = this.HIGHLIGHT_DIMENSION / this.project.view.zoom;
        return new paper.Path.Rectangle({
            point: segment.point.subtract(dimension / 2),
            size: new paper.Size(dimension, dimension),
            fillColor: 'rgba(255, 255, 255, 0.35)',
            locked: true
        });
    }

    public removeHighlightPoint() {
        if (this.highlightedPoint.index < 0) {
            return;
        }

        this.highlightedPoint.index = -1;
        this.highlightedPoint.shape.remove();
        this.highlightedPoint.shape = null;
    }

    public highlightPoint(index: number): void {
        const segment = this.getOuterContour().segments[index];

        if (this.highlightedPoint.index == index || !segment) {
            return;
        }

        if (this.highlightedPoint.index != index) {
            this.removeHighlightPoint();
        }

        this.highlightedPoint = {
            index,
            shape: this.generateHighlightForPoint(segment)
        };
    }

    /**
     * Assign a class to the polygon
     */
    public assignClass(c: Class): void {
        const color = new paper.Color(c.color);
        const fillColor = color.clone();
        fillColor.alpha = this.getFillOpacity();

        this.strokeColor = color;
        this.selectedColor = color;
        this.fillColor = fillColor;

        this.neuralaClass = c;
    }

    public hasNeuralaClass() {
        return this.neuralaClass.id !== null;
    }

    public scalePolygon(e: paper.MouseEvent) {
        if (this.project.polygonMovingInProgress || e.target.name != this.selectedManipulatorTool) {
            return;
        }

        const assetBounds = this.project.getAssetBounds();
        const assetScaling = this.project.getAsset().scaling;

        switch (e.target.name) {
            case 'scale_left':
                scaleWest(e, this.bounds, assetBounds, assetScaling);
                break;

            case 'scale_right':
                scaleEast(e, this.bounds, assetBounds, assetScaling);
                break;

            case 'scale_up':
                scaleNorth(e, this.bounds, assetBounds, assetScaling);
                break;

            case 'scale_down':
                scaleSouth(e, this.bounds, assetBounds, assetScaling);
                break;

            case 'scale_left_down':
                scaleSouthWest(e, this.bounds, assetBounds, assetScaling);
                break;

            case 'scale_left_up':
                scaleNorthWest(e, this.bounds, assetBounds, assetScaling);
                break;

            case 'scale_right_up':
                scaleNorthEast(e, this.bounds, assetBounds, assetScaling);
                break;

            case 'scale_right_down':
                scaleSouthEast(e, this.bounds, assetBounds, assetScaling);
                break;

            default:
                break;
        }
        this.updateBoundingBoxTools();
    }

    public generateBoundingBoxManipulationTools() {
        this.bbManipulationToolsVisible = true;
        const dimension = this.BB_MANIPULATOR_WIDTH / this.project.view.zoom;
        if (this.bbManipulationTools.length == 0) {
            for (const toolInfo of this.BB_TOOL_INFO) {
                const point = this.bounds[toolInfo[1]].subtract(dimension / 2);
                const rect = new paper.Path.Rectangle({
                    point,
                    size: new paper.Size(dimension, dimension),
                    fillColor: 'white',
                    strokeColor: 'black',
                    strokeWidth: 1 / this.project.view.zoom,
                    name: toolInfo[0],
                    // Explicitly marking this polygon lets us keep other tools
                    // from clobbering these box manipulators.
                    data: {
                        isBoundingBoxTool: true
                    }
                });

                if (toolInfo[1] === 'topLeft') {
                    this.updateBoundingBoxDimensionsText(point.x, point.y);
                }

                rect.on({
                    mousedown: e => {
                        this.project.polygonResizingInProgress = true;
                        this.selectedManipulatorTool = e.target.name;
                        this.isResizing = true;
                        this.boundingBoxResizeInProgress.next(true);
                    },
                    mouseup: e => {
                        this.project.polygonResizingInProgress = false;
                        this.selectedManipulatorTool = null;
                        this.isResizing = false;
                        this.boundingBoxResizeInProgress.next(false);
                    },
                    mousedrag: e => {
                        this.scalePolygon(e);
                    }
                });

                this.bbManipulationTools.push({
                    toolName: toolInfo[0],
                    obj: rect,
                    placement: toolInfo[1]
                });
            }
        } else {
            this.updateBoundingBoxTools();
        }
    }

    public updateBoundingBoxTools() {
        this.bbManipulationToolsVisible = true;
        for (const tool of this.bbManipulationTools) {
            this.updateBoundingBoxToolLocation(tool.obj, tool.placement);
            this.updateBoundingBoxToolZoom(tool.obj);
            if (tool.toolName === 'scale_left_up') {
                this.updateBoundingBoxDimensionsText(tool.obj.bounds.topLeft.x, tool.obj.bounds.topLeft.y);
            }
        }
    }

    private updateBoundingBoxToolZoom(tool: paper.Path.Rectangle) {
        const dimension = this.BB_MANIPULATOR_WIDTH / this.project.view.zoom;
        tool.bounds.size = new paper.Size(dimension, dimension);
        tool.strokeWidth = 1 / this.project.view.zoom;
    }

    private updateBoundingBoxToolLocation(tool: paper.Path.Rectangle, placement: string) {
        const dimension = this.BB_MANIPULATOR_WIDTH / this.project.view.zoom;
        tool.bounds.point = this.bounds[placement].subtract(dimension / 2);
    }

    private updateBoundingBoxDimensionsText(x: number, y: number) {
        const viewPosition = this.view.projectToView(new paper.Point(x, y));
        this.dimensionTextLocation = new paper.Point(viewPosition.x + 10, viewPosition.y + 24);
    }

    public getDimensionTextX(): number {
        return this.dimensionTextLocation.x;
    }

    public getDimensionTextY(): number {
        return this.dimensionTextLocation.y;
    }

    public removeBoundingBoxManipulationTools() {
        this.bbManipulationToolsVisible = false;
        if (!this.isResizing) {
            this.bbManipulationToolsVisible = false;
            this.bbManipulationTools.map(tool => {
                tool.obj.remove();
            });
            this.bbManipulationTools = [];
        }
    }

    public isPolygonBoundingBox(): boolean {
        if (this.children.length == 0) {
            return false;
        }

        const segments = (this.children[0] as paper.Path).segments;

        if (segments.length != 4) {
            return false;
        }

        let xCoordinates: number[] = [];
        let yCoordinates: number[] = [];

        segments.map(seg => {
            xCoordinates.push(seg.point.x);
            yCoordinates.push(seg.point.y);
        });

        xCoordinates = xCoordinates.sort();
        yCoordinates = yCoordinates.sort();

        return (
            xCoordinates[0] == xCoordinates[1] &&
            xCoordinates[2] == xCoordinates[3] &&
            yCoordinates[0] == yCoordinates[1] &&
            yCoordinates[2] == yCoordinates[3]
        );
    }

    public getFillOpacity(): number {
        // Bounding Box fill should be transparent, but leave a tiny bit so the box area can still be clickable
        return this.isBoundingBox ? 0.01 : 0.4;
    }
}



// WEBPACK FOOTER //
// ./src/components/workspace/objects/polygon.ts